<template>
  <BlockFieldset class="project-power-company">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <div v-else>
      <b-form-group
        horizontal
        :label="$t('powerCompany')"
        label-for="powerCompany"
        label-class="text-md-right"
        :label-cols="4"
        breakpoint="md"
        :invalid-feedback="$t('preconditionNA')"
        :state="isNetRegistrationRequirementFulfilled('powerCompany')"
      >
        <multiselect
          :placeholder="$t('powerCompany')"
          v-model="project.powerCompany"
          id="powerCompany"
          :options="powerCompanyOptions"
          :allow-empty="false"
          :show-labels="false"
          track-by="company"
          label="company"
          :disabled="isBlockReadOnly"
        >
        </multiselect>
      </b-form-group>

      <legend>
        <strong>{{ $t('powerCompanyMasterData') }}</strong>
      </legend>
      <div class="form-grid-container-left">
        <template v-for="field in MASTER_DATA_TEXT_FIELDS">
          <div class="label" :key="`${field}-label`">{{ $t(field) }}</div>
          <div v-if="masterDataValue(field)" :key="field">
            <strong>{{ masterDataValue(field) }}</strong>
          </div>
          <div v-else :key="field">{{ $t('not_applicable') }}</div>
        </template>

        <div class="label">{{ $t('website') }}</div>
        <div v-if="masterDataValue('website')">
          <a :href="masterDataValue('website')" target="_blank">{{ masterDataValue('website') }}</a>
        </div>
        <div v-else>{{ $t('not_applicable') }}</div>

        <div class="label">{{ $t('description') }}</div>
        <div v-if="masterDataValue('description')" v-html="masterDataValue('description')"></div>
        <div v-else>{{ $t('not_applicable') }}</div>
      </div>

      <div v-if="!isBlockReadOnly" class="text-right mt-2">
        <b-button variant="bg" :disabled="isSaving" @click="save">
          {{ $t('saveNamed', { name: $t('powerCompany') }) }}
        </b-button>
      </div>
    </div>
  </BlockFieldset>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import BlockFieldset from '@/components/Access/BlockFieldset.vue';
import { AccessBlockMixin } from '@/mixins/accessBlock';
import { BlockSaveMixin } from '@/mixins/blockSave';
import { isRequirementFulfilled } from '@/helpers/registrationRequirements';
import { toProjectPowerCompanyInput } from './projectPowerCompanyInput';

/** Number of the unsaved project (ProjectEdit `_new`): the block needs a saved project. */
const NEW_PROJECT_NUMBER = '_new';

/** Master data of the selected power company shown as plain text (website and description follow). */
const MASTER_DATA_TEXT_FIELDS = ['street', 'zip', 'city'];

/**
 * Block project-power-company ("EVU", see feature page projekt-evu): the power company of the
 * project and its master data, read-only. The select keeps `allow-empty="false"`; clearing is only
 * possible through the API. The editor loads the power company list itself (the block brings the
 * read on `editor`); a reader sees only the selected one. The marker NA comes from the block's
 * requirement list. On the unsaved project it shows only a hint. Emits `dirty` and `saved`.
 */
export default {
  name: 'ProjectPowerCompany',
  components: { BlockFieldset, Multiselect },
  mixins: [AccessBlockMixin, BlockSaveMixin],
  props: {
    blockId: { type: String, required: true },
    project: { type: Object, required: true },
  },
  data() {
    return { MASTER_DATA_TEXT_FIELDS };
  },
  computed: {
    ...mapGetters(['getPowerCompanies']),
    isNewProject() {
      return this.project.number === NEW_PROJECT_NUMBER;
    },
    blockInput() {
      return toProjectPowerCompanyInput(this.project);
    },
    powerCompanyOptions() {
      return this.isBlockReadOnly ? [] : this.getPowerCompanies;
    },
  },
  methods: {
    ...mapActions(['updateProjectPowerCompany', 'fetchPowerCompanies', 'message']),
    saveBlockInput(input) {
      return this.updateProjectPowerCompany({ projectNumber: this.project.number, input });
    },
    masterDataValue(field) {
      return this.project.powerCompany?.[field];
    },
    isNetRegistrationRequirementFulfilled(name) {
      return isRequirementFulfilled(this.project.netRegistrationRequirements, name);
    },
    /** A failed fetch leaves the select empty; the block stays usable with the stored value. */
    async loadPowerCompanies() {
      try {
        await this.fetchPowerCompanies();
      } catch (error) {
        console.error('Error loading power companies:', error);
        this.message({ message: error.message, type: 'danger', lifetime: 4 });
      }
    },
  },
  mounted() {
    if (this.isNewProject || this.isBlockReadOnly) {
      return;
    }
    this.loadPowerCompanies();
  },
};
</script>

<style scoped lang="scss">
.form-grid-container-left {
  display: grid;
  grid-template-columns: 1fr 2fr;
  column-gap: 1em;
  row-gap: 1em;
}
.form-grid-container-left .label {
  text-align: right;
}
</style>
